import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { all_routes } from '../../../router/all_routes';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import reportService from '../../../services/reports/report.service';
import { LeaveReportDto } from '../../../types/report.types';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { handleApiError } from '../../../utils/errorHandler';
import { useTranslation } from 'react-i18next';
import FooterSection from '../../shared/components/footerSection';
import { registerLocale, setDefaultLocale } from "react-datepicker";
import trLocale from 'date-fns/locale/tr';
import enLocale from 'date-fns/locale/en-US';
import "react-datepicker/dist/react-datepicker.css";
import i18n from '../../../i18n/config';
import { Select, Table, Tag, Button, Modal } from 'antd';
import { FilterOutlined, EyeOutlined } from '@ant-design/icons';
import leaveService from '../../../services/leave/leave.service';
import { LeaveDto } from '../../../types/leaves.types';
import dayjs from 'dayjs';

const LeaveReport = () => {
    const { t } = useTranslation();
    const [reportData, setReportData] = useState<LeaveReportDto | null>(null);
    const [allLeaves, setAllLeaves] = useState<LeaveDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState(new Date(new Date().setMonth(new Date().getMonth() - 1)));
    const [endDate, setEndDate] = useState(new Date());
    // Kıyaslama için state'ler
    const [departments, setDepartments] = useState<string[]>([]);
    const [branches, setBranches] = useState<string[]>([]);
    const [selectedDepartmentsForComparison, setSelectedDepartmentsForComparison] = useState<string[]>([]);
    const [selectedBranchesForComparison, setSelectedBranchesForComparison] = useState<string[]>([]);
    // Çalışan listesi için state'ler
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
    const [isEmployeeLeaveModalVisible, setIsEmployeeLeaveModalVisible] = useState(false);

    useEffect(() => {
        // Türkçe ve İngilizce locale'leri kaydet
        registerLocale('tr', trLocale as any); // Type assertion ile hatayı gideriyoruz
        registerLocale('en', enLocale as any);

        // Mevcut dile göre varsayılan locale'i ayarla
        setDefaultLocale(i18n.language === 'tr' ? 'tr' : 'en');
    }, [i18n.language]);



    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const data = await reportService.getLeaveReport(
                    startDate.toISOString().split('T')[0],
                    endDate.toISOString().split('T')[0]
                );
                setReportData(data);

                // Tüm izinleri çek (departman/şube bilgileri için)
                const leaves = await leaveService.getLeaves();
                setAllLeaves(leaves);

                // Departman ve şube listelerini oluştur
                const uniqueDepartments = Array.from(
                    new Set(
                        leaves.flatMap(leave =>
                            leave.user?.department?.map((dept: { name: string }) => dept.name) || []
                        )
                    )
                ) as string[];
                const uniqueBranches = Array.from(
                    new Set(
                        leaves.flatMap(leave =>
                            leave.user?.branch?.map((branch: { name: string }) => branch.name) || []
                        )
                    )
                ) as string[];
                setDepartments(uniqueDepartments);
                setBranches(uniqueBranches);
            } catch (error: any) {
                handleApiError(error, t);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [startDate, endDate]);

    // İzin Tipi Dağılımı Chart
    const leaveTypeChartOptions: ApexOptions = {
        chart: {
            type: 'donut' as const,
            height: 350
        },
        labels: reportData ? Object.keys(reportData?.leaveTypeDistribution || {}).map(key => t(`leaveTypes.${key}`)) : [],
        colors: ['#4318FF', '#6AD2FF', '#05CD99', '#FFB547', '#E31A1A'],
        legend: {
            position: 'bottom'
        },
        plotOptions: {
            pie: {
                donut: {
                    size: '70%'
                }
            }
        }
    };

    // İzin Trendleri Chart
    const trendChartOptions: ApexOptions = {
        chart: {
            type: 'area' as const,
            height: 350,
            toolbar: {
                show: false
            }
        },
        dataLabels: {
            enabled: false
        },
        stroke: {
            curve: 'smooth',
            width: 2
        },
        colors: ['#4318FF'],
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.7,
                opacityTo: 0.3,
                stops: [0, 90, 100]
            }
        },
        xaxis: {
            categories: reportData ? Object.keys(reportData?.leaveTrends || {}) : [],
            labels: {
                rotate: -45,
                rotateAlways: true
            }
        },
        yaxis: {
            title: {
                text: t('leaveReport.leaveCount')
            }
        }
    };

    // Özet Kartlar için Hesaplamalar
    const calculateSummary = () => {
        if (!reportData || !reportData.leaveTypeDistribution || !reportData.leaveTrends)
            return { total: 0, mostUsed: '', peakLeaveDay: { date: '', count: 0 } };

        const total = Object.values(reportData.leaveTypeDistribution).reduce((a, b) => a + b, 0);

        // En çok kullanılan izin tipini bul, array boşsa boş string döndür
        const entries = Object.entries(reportData.leaveTypeDistribution);
        const mostUsed = entries.length > 0
            ? entries.reduce((a, b) => a[1] > b[1] ? a : b)[0]
            : '';

        // En yoğun izin gününü bul
        const trendEntries = Object.entries(reportData.leaveTrends);
        const peakLeaveDay = trendEntries.length > 0
            ? trendEntries.reduce((a, b) => a[1] > b[1] ? a : b)
            : ['', 0];

        // Tarihi formatla (2025-06-25 -> 25 Haziran 2025)
        const formatDate = (dateString: string) => {
            if (!dateString) return '';
            const date = new Date(dateString);
            return date.toLocaleDateString('tr-TR', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        };

        return {
            total,
            mostUsed,
            peakLeaveDay: {
                date: formatDate(peakLeaveDay[0] as string),
                count: peakLeaveDay[1] as number
            }
        };
    };

    const summary = calculateSummary();

    // Çalışan bazlı izin istatistiklerini hesapla
    const employeeLeaveStats = useMemo(() => {
        if (!allLeaves || allLeaves.length === 0) return [];

        // Tarih aralığına göre izinleri filtrele
        let filteredLeaves = allLeaves.filter(leave => {
            const leaveStartDate = dayjs(leave.startDate);
            const leaveEndDate = dayjs(leave.endDate);
            const rangeStart = dayjs(startDate);
            const rangeEnd = dayjs(endDate);

            return (
                (leaveStartDate.isSameOrAfter(rangeStart, 'day') && leaveStartDate.isSameOrBefore(rangeEnd, 'day')) ||
                (leaveEndDate.isSameOrAfter(rangeStart, 'day') && leaveEndDate.isSameOrBefore(rangeEnd, 'day')) ||
                (leaveStartDate.isBefore(rangeStart, 'day') && leaveEndDate.isAfter(rangeEnd, 'day'))
            );
        });

        // Departman ve şube kıyaslamasına göre filtrele
        if (selectedDepartmentsForComparison.length > 0) {
            filteredLeaves = filteredLeaves.filter(leave =>
                leave.user?.department?.some((dept: { name: string }) =>
                    selectedDepartmentsForComparison.includes(dept.name)
                )
            );
        } else if (selectedBranchesForComparison.length > 0) {
            filteredLeaves = filteredLeaves.filter(leave =>
                leave.user?.branch?.some((branch: { name: string }) =>
                    selectedBranchesForComparison.includes(branch.name)
                )
            );
        }

        // Çalışan bazında grupla
        const employeeMap = new Map<number, {
            id: number;
            name: string;
            department: string;
            branch: string;
            totalLeaves: number;
            leaveTypeCount: { [key: string]: number };
            leaves: LeaveDto[];
        }>();

        filteredLeaves.forEach(leave => {
            if (!leave.user?.id) return;

            const userId = leave.user.id;
            const userName = `${leave.user.firstName || ''} ${leave.user.lastName || ''}`.trim();
            const userDepartment = leave.user.department?.map((d: { name: string }) => d.name).join(', ') || '-';
            const userBranch = leave.user.branch?.map((b: { name: string }) => b.name).join(', ') || '-';

            if (!employeeMap.has(userId)) {
                employeeMap.set(userId, {
                    id: userId,
                    name: userName,
                    department: userDepartment,
                    branch: userBranch,
                    totalLeaves: 0,
                    leaveTypeCount: {},
                    leaves: []
                });
            }

            const employee = employeeMap.get(userId)!;
            employee.totalLeaves++;
            employee.leaveTypeCount[leave.type] = (employee.leaveTypeCount[leave.type] || 0) + 1;
            employee.leaves.push(leave);
        });

        return Array.from(employeeMap.values()).sort((a, b) => b.totalLeaves - a.totalLeaves);
    }, [allLeaves, startDate, endDate, selectedDepartmentsForComparison, selectedBranchesForComparison]);

    // Seçili çalışanın izinlerini filtrele
    const selectedEmployeeLeaves = useMemo(() => {
        if (!selectedEmployeeId) return [];
        return employeeLeaveStats.find(emp => emp.id === selectedEmployeeId)?.leaves || [];
    }, [selectedEmployeeId, employeeLeaveStats]);

    // Kıyaslama verilerini hesapla
    const comparisonData = useMemo(() => {
        if (selectedDepartmentsForComparison.length === 0 && selectedBranchesForComparison.length === 0) {
            return null;
        }

        // Karışık seçim kontrolü
        if (selectedDepartmentsForComparison.length > 0 && selectedBranchesForComparison.length > 0) {
            return null; // Karışık seçim yapılamaz
        }

        // Tarih aralığına göre izinleri filtrele
        const filteredLeaves = allLeaves.filter(leave => {
            const leaveStartDate = dayjs(leave.startDate);
            const leaveEndDate = dayjs(leave.endDate);
            const rangeStart = dayjs(startDate);
            const rangeEnd = dayjs(endDate);

            // İzin tarihi aralığa düşüyor mu kontrol et
            return (
                (leaveStartDate.isSameOrAfter(rangeStart, 'day') && leaveStartDate.isSameOrBefore(rangeEnd, 'day')) ||
                (leaveEndDate.isSameOrAfter(rangeStart, 'day') && leaveEndDate.isSameOrBefore(rangeEnd, 'day')) ||
                (leaveStartDate.isBefore(rangeStart, 'day') && leaveEndDate.isAfter(rangeEnd, 'day'))
            );
        });

        const comparisonItems: Array<{
            name: string;
            type: 'department' | 'branch';
            totalLeaves: number;
            approvedLeaves: number;
            pendingLeaves: number;
            rejectedLeaves: number;
            leaveTypeDistribution: { [key: string]: number };
            averageLeaveDuration: number; // Ortalama izin süresi (gün)
            employees: number;
        }> = [];

        if (selectedDepartmentsForComparison.length > 0) {
            // Departman kıyaslaması
            selectedDepartmentsForComparison.forEach(deptName => {
                const deptLeaves = filteredLeaves.filter(leave =>
                    leave.user?.department?.some((dept: { name: string }) => dept.name === deptName)
                );

                const leaveTypeDist: { [key: string]: number } = {};
                let totalDuration = 0;
                const uniqueEmployees = new Set<number>();

                deptLeaves.forEach(leave => {
                    if (leave.user?.id) uniqueEmployees.add(leave.user.id);
                    
                    // İzin tipi dağılımı
                    const type = leave.type;
                    leaveTypeDist[type] = (leaveTypeDist[type] || 0) + 1;

                    // İzin süresi hesapla
                    const duration = dayjs(leave.endDate).diff(dayjs(leave.startDate), 'day') + 1;
                    totalDuration += duration;
                });

                const approvedCount = deptLeaves.filter(l => l.status === 'approved').length;
                const pendingCount = deptLeaves.filter(l => l.status === 'pending' || l.status === 'pending_hierarchy_approval').length;
                const rejectedCount = deptLeaves.filter(l => l.status === 'rejected').length;
                const avgDuration = deptLeaves.length > 0 ? totalDuration / deptLeaves.length : 0;

                comparisonItems.push({
                    name: deptName,
                    type: 'department',
                    totalLeaves: deptLeaves.length,
                    approvedLeaves: approvedCount,
                    pendingLeaves: pendingCount,
                    rejectedLeaves: rejectedCount,
                    leaveTypeDistribution: leaveTypeDist,
                    averageLeaveDuration: Math.round(avgDuration * 10) / 10,
                    employees: uniqueEmployees.size
                });
            });
        } else if (selectedBranchesForComparison.length > 0) {
            // Şube kıyaslaması
            selectedBranchesForComparison.forEach(branchName => {
                const branchLeaves = filteredLeaves.filter(leave =>
                    leave.user?.branch?.some((branch: { name: string }) => branch.name === branchName)
                );

                const leaveTypeDist: { [key: string]: number } = {};
                let totalDuration = 0;
                const uniqueEmployees = new Set<number>();

                branchLeaves.forEach(leave => {
                    if (leave.user?.id) uniqueEmployees.add(leave.user.id);
                    
                    // İzin tipi dağılımı
                    const type = leave.type;
                    leaveTypeDist[type] = (leaveTypeDist[type] || 0) + 1;

                    // İzin süresi hesapla
                    const duration = dayjs(leave.endDate).diff(dayjs(leave.startDate), 'day') + 1;
                    totalDuration += duration;
                });

                const approvedCount = branchLeaves.filter(l => l.status === 'approved').length;
                const pendingCount = branchLeaves.filter(l => l.status === 'pending' || l.status === 'pending_hierarchy_approval').length;
                const rejectedCount = branchLeaves.filter(l => l.status === 'rejected').length;
                const avgDuration = branchLeaves.length > 0 ? totalDuration / branchLeaves.length : 0;

                comparisonItems.push({
                    name: branchName,
                    type: 'branch',
                    totalLeaves: branchLeaves.length,
                    approvedLeaves: approvedCount,
                    pendingLeaves: pendingCount,
                    rejectedLeaves: rejectedCount,
                    leaveTypeDistribution: leaveTypeDist,
                    averageLeaveDuration: Math.round(avgDuration * 10) / 10,
                    employees: uniqueEmployees.size
                });
            });
        }

        return comparisonItems.length > 0 ? comparisonItems : null;
    }, [selectedDepartmentsForComparison, selectedBranchesForComparison, allLeaves, startDate, endDate]);

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Breadcrumb */}
                <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                    <div className="my-auto mb-2">
                        <h2 className="mb-1">{t('leaveReport.pageTitle')}</h2>
                        <nav>
                            <ol className="breadcrumb mb-0">
                                <li className="breadcrumb-item">
                                    <Link to={all_routes.adminDashboard}>
                                        <i className="ti ti-smart-home" />
                                    </Link>
                                </li>
                                <li className="breadcrumb-item">{t('leaveReport.breadcrumb.reports')}</li>
                                <li className="breadcrumb-item active" aria-current="page">
                                    {t('leaveReport.pageTitle')}
                                </li>
                            </ol>
                        </nav>
                    </div>
                    <div className="d-flex my-xl-auto right-content align-items-center">
                        <div className="me-2">
                            <div className="d-flex align-items-center">
                                <DatePicker
                                    locale={i18n.language === 'tr' ? 'tr' : 'en'} // Locale'i ayarla
                                    selected={startDate}
                                    onChange={(date: Date | null) => date && setStartDate(date)}
                                    selectsStart
                                    startDate={startDate}
                                    endDate={endDate}
                                    className="form-control form-control-sm"
                                    dateFormat="dd/MM/yyyy"
                                />
                                <span className="mx-2">-</span>
                                <DatePicker
                                    locale={i18n.language === 'tr' ? 'tr' : 'en'} // Locale'i ayarla
                                    selected={endDate}
                                    onChange={(date: Date | null) => date && setEndDate(date)}
                                    selectsEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    minDate={startDate}
                                    className="form-control form-control-sm"
                                    dateFormat="dd/MM/yyyy"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center p-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">{t('common.loading')}</span>
                        </div>
                    </div>
                ) : reportData ? (
                    <div className="row">
                        {/* Özet Kartlar */}
                        <div className="col-xl-4 col-md-6 d-flex">
                            <div className="card flex-fill">
                                <div className="card-body">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="avatar avatar-lg me-3 bg-primary-transparent">
                                            <i className="ti ti-calendar-stats fs-20 text-primary"></i>
                                        </div>
                                        <div>
                                            <span className="fs-14 fw-normal">{t('leaveReport.totalLeaves')}</span>
                                            <h5 className="mb-0">{summary.total}</h5>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <div className="flex-fill">
                                            <div className="d-flex align-items-center">
                                                <span className="fs-13 text-muted">
                                                    {t('leaveReport.totalLeavesInRange')}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-4 col-md-6 d-flex">
                            <div className="card flex-fill">
                                <div className="card-body">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="avatar avatar-lg me-3 bg-success-transparent">
                                            <i className="ti ti-chart-pie fs-20 text-success"></i>
                                        </div>
                                        <div>
                                            <span className="fs-14 fw-normal">{t('leaveReport.mostUsedLeave')}</span>
                                            <h5 className="mb-0">{summary.total > 0 ? t(`leaveTypes.${summary.mostUsed}`) : t('leaveReport.noData')}</h5>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <div className="flex-fill">
                                            <div className="d-flex align-items-center">
                                                <span className="fs-13 text-muted">
                                                    {t('leaveReport.mostPreferredLeaveType')}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-4 col-md-6 d-flex">
                            <div className="card flex-fill">
                                <div className="card-body">
                                    <div className="d-flex align-items-center mb-3">
                                        <div className="avatar avatar-lg me-3 bg-warning-transparent">
                                            <i className="ti ti-calendar-event fs-20 text-warning"></i>
                                        </div>
                                        <div className="flex-fill">
                                            <span className="fs-14 fw-normal text-muted">{t('leaveReport.peakLeaveDayTitle')}</span>
                                            <div className="d-flex align-items-center mt-1">
                                                <h4 className="mb-0 me-2">{summary.peakLeaveDay.count}</h4>
                                                <span className="fs-16">
                                                    {t('common.person', { count: summary.peakLeaveDay.count })}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="border-top pt-3">
                                        <div className="d-flex align-items-center">
                                            <i className="ti ti-calendar-check fs-16 text-muted me-2"></i>
                                            <span className="fs-13 fw-medium text-dark">
                                                {summary.peakLeaveDay.date || t('leaveReport.noData')}
                                            </span>
                                        </div>
                                        <div className="d-flex align-items-center mt-2">
                                            <i className="ti ti-info-circle fs-16 text-muted me-2"></i>
                                            <span className="fs-12 text-muted">
                                                {t('leaveReport.peakLeaveDayDescription')}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* İzin Tipi Dağılımı */}
                        <div className="col-xl-6 d-flex">
                            <div className="card flex-fill">
                                <div className="card-header">
                                    <h5 className="card-title">{t('leaveReport.leaveTypeDistribution')}</h5>
                                </div>
                                <div className="card-body">
                                    <ReactApexChart
                                        options={leaveTypeChartOptions}
                                        series={Object.values(reportData?.leaveTypeDistribution || {})}
                                        type="donut"
                                        height={350}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* İzin Trendleri */}
                        <div className="col-xl-6 d-flex">
                            <div className="card flex-fill">
                                <div className="card-header">
                                    <h5 className="card-title">İzin Tarihi Dağılımı</h5>
                                </div>
                                <div className="card-body">
                                    <ReactApexChart
                                        options={trendChartOptions}
                                        series={[{
                                            name: t('leaveReport.leaveCount'),
                                            data: Object.values(reportData?.leaveTrends || {})
                                        }]}
                                        type="area"
                                        height={350}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="alert alert-danger">
                        {t('leaveReport.errorLoadingData')}
                    </div>
                )}

                {/* Kıyaslama Bölümü - En Alta */}
                <div className="card mb-4 mt-4" style={{ borderLeft: '4px solid #1890ff' }}>
                    <div className="card-body">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                            <div>
                                <h6 className="mb-1 fw-bold">
                                    <i className="ti ti-chart-bar me-2 text-primary"></i>
                                    Departman ve Şube Kıyaslaması
                                </h6>
                                <small className="text-muted">Seçtiğiniz departmanlar veya şubeler arasında izin kullanım karşılaştırması yapabilirsiniz</small>
                            </div>
                        </div>

                        <div className="row g-3 mb-3">
                            <div className="col-md-6">
                                <label className="form-label mb-2 fs-13 fw-semibold text-dark">
                                    <i className="ti ti-building-skyscraper me-1 text-primary"></i>
                                    Departman Kıyaslaması
                                </label>
                                <Select
                                    mode="multiple"
                                    placeholder="Kıyaslamak için departman seçin"
                                    allowClear
                                    style={{ width: '100%', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}
                                    onChange={(value) => {
                                        setSelectedDepartmentsForComparison(value);
                                        if (value.length > 0) {
                                            setSelectedBranchesForComparison([]);
                                        }
                                    }}
                                    value={selectedDepartmentsForComparison}
                                    disabled={selectedBranchesForComparison.length > 0}
                                    suffixIcon={<i className="ti ti-chevron-down text-primary"></i>}
                                    dropdownStyle={{ borderRadius: '8px', boxShadow: '0 6px 16px rgba(0,0,0,0.1)' }}
                                    maxTagCount={2}
                                    maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} departman`}
                                >
                                    {departments?.map(dept => (
                                        <Select.Option key={dept} value={dept}>
                                            <div className="d-flex align-items-center">
                                                <span className="me-2 rounded-circle d-inline-block" style={{ width: "8px", height: "8px", backgroundColor: "#1890ff" }}></span>
                                                {dept}
                                            </div>
                                        </Select.Option>
                                    ))}
                                </Select>
                            </div>
                            <div className="col-md-6">
                                <label className="form-label mb-2 fs-13 fw-semibold text-dark">
                                    <i className="ti ti-map-pin me-1 text-danger"></i>
                                    Şube Kıyaslaması
                                </label>
                                <Select
                                    mode="multiple"
                                    placeholder="Kıyaslamak için şube seçin"
                                    allowClear
                                    style={{ width: '100%', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}
                                    onChange={(value) => {
                                        setSelectedBranchesForComparison(value);
                                        if (value.length > 0) {
                                            setSelectedDepartmentsForComparison([]);
                                        }
                                    }}
                                    value={selectedBranchesForComparison}
                                    disabled={selectedDepartmentsForComparison.length > 0}
                                    suffixIcon={<i className="ti ti-chevron-down text-danger"></i>}
                                    dropdownStyle={{ borderRadius: '8px', boxShadow: '0 6px 16px rgba(0,0,0,0.1)' }}
                                    maxTagCount={2}
                                    maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} şube`}
                                >
                                    {branches.map(branch => (
                                        <Select.Option key={branch} value={branch}>
                                            <div className="d-flex align-items-center">
                                                <span className="me-2 rounded-circle d-inline-block" style={{ width: "8px", height: "8px", backgroundColor: "#ff4d4f" }}></span>
                                                {branch}
                                            </div>
                                        </Select.Option>
                                    ))}
                                </Select>
                            </div>
                        </div>

                        {/* Karışık seçim uyarısı */}
                        {selectedDepartmentsForComparison.length > 0 && selectedBranchesForComparison.length > 0 && (
                            <div className="alert alert-warning mb-3" role="alert">
                                <i className="ti ti-alert-triangle me-2"></i>
                                Bir şube ile bir departmanı kıyaslamak mantıksızdır. Lütfen sadece departmanlar veya sadece şubeler seçin.
                            </div>
                        )}

                        {/* Kıyaslama Sonuçları */}
                        {comparisonData && comparisonData.length > 0 && (
                            <div className="mt-4">
                                <h6 className="mb-3 fw-semibold">
                                    <i className="ti ti-chart-line me-2"></i>
                                    Kıyaslama Sonuçları
                                </h6>
                                
                                {/* Kıyaslama Grafiği - İzin Tipi Dağılımı */}
                                <div className="card mb-3" style={{ backgroundColor: '#f8f9fa' }}>
                                    <div className="card-body">
                                        <h6 className="mb-3">İzin Tipi Dağılımı</h6>
                                        <ReactApexChart
                                            options={{
                                                chart: {
                                                    type: 'bar',
                                                    height: 350,
                                                    toolbar: { show: true },
                                                    animations: { enabled: true, speed: 800 }
                                                },
                                                plotOptions: {
                                                    bar: {
                                                        horizontal: false,
                                                        columnWidth: '60%',
                                                        borderRadius: 6,
                                                        dataLabels: { position: 'top' }
                                                    }
                                                },
                                                dataLabels: {
                                                    enabled: true,
                                                    formatter: (val: number) => val > 0 ? `${val}` : '',
                                                    style: { fontSize: '12px', fontWeight: 600, colors: ['#000'] }
                                                },
                                                xaxis: {
                                                    categories: comparisonData.map(item => item.name),
                                                    labels: { style: { fontSize: '12px' } }
                                                },
                                                yaxis: {
                                                    title: { text: 'İzin Sayısı' },
                                                    labels: { style: { fontSize: '12px' } }
                                                },
                                                colors: ['#4318FF', '#6AD2FF', '#05CD99', '#FFB547', '#E31A1A', '#AF52DE', '#FF9500'],
                                                legend: {
                                                    position: 'top',
                                                    horizontalAlign: 'right',
                                                    fontSize: '12px'
                                                },
                                                tooltip: {
                                                    y: { formatter: (val: number) => `${val} izin` }
                                                },
                                                grid: { borderColor: '#e7e7e7' }
                                            }}
                                            series={(() => {
                                                // Tüm izin tiplerini topla
                                                const allTypes = new Set<string>();
                                                comparisonData.forEach(item => {
                                                    Object.keys(item.leaveTypeDistribution).forEach(type => allTypes.add(type));
                                                });
                                                
                                                return Array.from(allTypes).map(type => ({
                                                    name: t(`leaveTypes.${type}`),
                                                    data: comparisonData.map(item => item.leaveTypeDistribution[type] || 0)
                                                }));
                                            })()}
                                            type="bar"
                                            height={350}
                                        />
                                    </div>
                                </div>

                                {/* Kıyaslama Tablosu */}
                                <div className="table-responsive">
                                    <Table
                                        dataSource={comparisonData.map((item, index) => ({ ...item, key: index }))}
                                        columns={[
                                            {
                                                title: comparisonData[0]?.type === 'department' ? 'Departman' : 'Şube',
                                                dataIndex: 'name',
                                                key: 'name',
                                                render: (text: string) => (
                                                    <strong style={{ color: comparisonData[0]?.type === 'department' ? '#1890ff' : '#ff4d4f' }}>
                                                        {text}
                                                    </strong>
                                                )
                                            },
                                            {
                                                title: 'Çalışan Sayısı',
                                                dataIndex: 'employees',
                                                key: 'employees',
                                                align: 'center' as const,
                                                sorter: (a, b) => a.employees - b.employees
                                            },
                                            {
                                                title: 'Toplam İzin',
                                                dataIndex: 'totalLeaves',
                                                key: 'totalLeaves',
                                                align: 'center' as const,
                                                render: (val: number) => <strong style={{ fontSize: '16px' }}>{val}</strong>,
                                                sorter: (a, b) => a.totalLeaves - b.totalLeaves,
                                                defaultSortOrder: 'descend' as const
                                            },
                                            {
                                                title: 'Onaylanan',
                                                dataIndex: 'approvedLeaves',
                                                key: 'approvedLeaves',
                                                align: 'center' as const,
                                                render: (val: number) => <Tag color="success">{val}</Tag>,
                                                sorter: (a, b) => a.approvedLeaves - b.approvedLeaves
                                            },
                                            {
                                                title: 'Beklemede',
                                                dataIndex: 'pendingLeaves',
                                                key: 'pendingLeaves',
                                                align: 'center' as const,
                                                render: (val: number) => <Tag color="warning">{val}</Tag>,
                                                sorter: (a, b) => a.pendingLeaves - b.pendingLeaves
                                            },
                                            {
                                                title: 'Reddedilen',
                                                dataIndex: 'rejectedLeaves',
                                                key: 'rejectedLeaves',
                                                align: 'center' as const,
                                                render: (val: number) => <Tag color="error">{val}</Tag>,
                                                sorter: (a, b) => a.rejectedLeaves - b.rejectedLeaves
                                            },
                                            {
                                                title: 'Ortalama İzin Süresi (Gün)',
                                                dataIndex: 'averageLeaveDuration',
                                                key: 'averageLeaveDuration',
                                                align: 'center' as const,
                                                render: (val: number) => <span>{val} gün</span>,
                                                sorter: (a, b) => a.averageLeaveDuration - b.averageLeaveDuration
                                            }
                                        ]}
                                        pagination={false}
                                        size="small"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Çalışan Listesi Bölümü */}
                <div className="card mb-4 mt-4">
                    <div className="card-header">
                        <h5 className="card-title mb-0">
                            <i className="ti ti-users me-2"></i>
                            Çalışan İzin Listesi
                        </h5>
                    </div>
                    <div className="card-body">
                        <div className="table-responsive">
                            <Table
                                dataSource={employeeLeaveStats.map((emp, index) => ({ ...emp, key: index }))}
                                columns={[
                                    {
                                        title: 'Çalışan',
                                        dataIndex: 'name',
                                        key: 'name',
                                        render: (text: string, record: any) => (
                                            <div>
                                                <strong>{text}</strong>
                                                <div className="text-muted fs-12 mt-1">
                                                    <span className="me-3">
                                                        <i className="ti ti-building me-1"></i>
                                                        {record.department}
                                                    </span>
                                                    <span>
                                                        <i className="ti ti-map-pin me-1"></i>
                                                        {record.branch}
                                                    </span>
                                                </div>
                                            </div>
                                        )
                                    },
                                    {
                                        title: 'Toplam İzin',
                                        dataIndex: 'totalLeaves',
                                        key: 'totalLeaves',
                                        align: 'center' as const,
                                        render: (val: number) => (
                                            <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                                                {val}
                                            </Tag>
                                        ),
                                        sorter: (a, b) => a.totalLeaves - b.totalLeaves,
                                        defaultSortOrder: 'descend' as const
                                    },
                                    {
                                        title: 'İzin Tipi Dağılımı',
                                        key: 'leaveTypeDistribution',
                                        render: (_: any, record: any) => {
                                            const types = Object.entries(record.leaveTypeCount);
                                            return (
                                                <div className="d-flex flex-wrap gap-2">
                                                    {types.map(([type, count]) => (
                                                        <Tag key={type} color={
                                                            type === 'annual' ? 'green' :
                                                            type === 'medical' ? 'red' :
                                                            type === 'hourly' ? 'blue' :
                                                            type === 'special' ? 'orange' :
                                                            type === 'paternity' ? 'purple' :
                                                            'default'
                                                        }>
                                                            {t(`leaveTypes.${type}`)}: {count as number}
                                                        </Tag>
                                                    ))}
                                                </div>
                                            );
                                        }
                                    },
                                    {
                                        title: 'İşlemler',
                                        key: 'actions',
                                        align: 'center' as const,
                                        width: 120,
                                        render: (_: any, record: any) => (
                                            <Button
                                                type="primary"
                                                icon={<EyeOutlined />}
                                                size="small"
                                                onClick={() => {
                                                    setSelectedEmployeeId(record.id);
                                                    setIsEmployeeLeaveModalVisible(true);
                                                }}
                                            >
                                                Detay
                                            </Button>
                                        )
                                    }
                                ]}
                                pagination={{
                                    pageSize: 10,
                                    showSizeChanger: true,
                                    showTotal: (total) => `Toplam ${total} çalışan`,
                                    pageSizeOptions: ['10', '20', '50', '100']
                                }}
                                size="small"
                            />
                        </div>
                    </div>
                </div>

                {/* Çalışan İzin Detay Modal */}
                <Modal
                    title={
                        <div className="d-flex align-items-center">
                            <div className="avatar avatar-sm me-2 bg-primary-transparent">
                                <i className="ti ti-user text-primary"></i>
                            </div>
                            <div>
                                <h5 className="mb-0">
                                    {selectedEmployeeId ? employeeLeaveStats.find(emp => emp.id === selectedEmployeeId)?.name : ''}
                                </h5>
                                <small className="text-muted">İzin Detayları</small>
                            </div>
                        </div>
                    }
                    open={isEmployeeLeaveModalVisible}
                    onCancel={() => {
                        setIsEmployeeLeaveModalVisible(false);
                        setSelectedEmployeeId(null);
                    }}
                    footer={null}
                    width={1000}
                    style={{ top: 20 }}
                >
                    {selectedEmployeeLeaves.length > 0 ? (
                        <div>
                            {/* Özet Kartlar */}
                            <div className="row g-3 mb-4">
                                <div className="col-md-4">
                                    <div className="card border-0 shadow-sm">
                                        <div className="card-body text-center">
                                            <div className="avatar avatar-lg mx-auto mb-2 bg-primary-transparent">
                                                <i className="ti ti-calendar-stats fs-24 text-primary"></i>
                                            </div>
                                            <h4 className="mb-1">{selectedEmployeeLeaves.length}</h4>
                                            <p className="text-muted mb-0 fs-13">Toplam İzin</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card border-0 shadow-sm">
                                        <div className="card-body text-center">
                                            <div className="avatar avatar-lg mx-auto mb-2 bg-success-transparent">
                                                <i className="ti ti-check fs-24 text-success"></i>
                                            </div>
                                            <h4 className="mb-1">
                                                <Tag color="success" style={{ fontSize: '16px', padding: '4px 12px' }}>
                                                    {selectedEmployeeLeaves.filter(l => l.status === 'approved').length}
                                                </Tag>
                                            </h4>
                                            <p className="text-muted mb-0 fs-13">Onaylanan</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card border-0 shadow-sm">
                                        <div className="card-body text-center">
                                            <div className="avatar avatar-lg mx-auto mb-2 bg-warning-transparent">
                                                <i className="ti ti-clock fs-24 text-warning"></i>
                                            </div>
                                            <h4 className="mb-1">
                                                <Tag color="warning" style={{ fontSize: '16px', padding: '4px 12px' }}>
                                                    {selectedEmployeeLeaves.filter(l => l.status === 'pending' || l.status === 'pending_hierarchy_approval').length}
                                                </Tag>
                                            </h4>
                                            <p className="text-muted mb-0 fs-13">Beklemede</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* İzin Listesi */}
                            <div className="table-responsive">
                                <Table
                                    dataSource={selectedEmployeeLeaves.map((leave, index) => ({ ...leave, key: index }))}
                                    columns={[
                                        {
                                            title: 'İzin Tipi',
                                            dataIndex: 'type',
                                            key: 'type',
                                            width: 150,
                                            render: (type: string) => (
                                                <Tag color={
                                                    type === 'annual' ? 'green' :
                                                    type === 'medical' ? 'red' :
                                                    type === 'hourly' ? 'blue' :
                                                    type === 'special' ? 'orange' :
                                                    type === 'paternity' ? 'purple' :
                                                    'default'
                                                } style={{ fontSize: '13px', padding: '4px 10px' }}>
                                                    {t(`leaveTypes.${type}`)}
                                                </Tag>
                                            )
                                        },
                                        {
                                            title: 'Başlangıç Tarihi',
                                            dataIndex: 'startDate',
                                            key: 'startDate',
                                            width: 140,
                                            render: (date: string, record: LeaveDto) => (
                                                <div>
                                                    <div className="fw-semibold">{dayjs(date).format('DD/MM/YYYY')}</div>
                                                    {record.startTime && (
                                                        <div className="text-muted fs-12">
                                                            <i className="ti ti-clock me-1"></i>
                                                            {record.startTime}
                                                        </div>
                                                    )}
                                                </div>
                                            )
                                        },
                                        {
                                            title: 'Bitiş Tarihi',
                                            dataIndex: 'endDate',
                                            key: 'endDate',
                                            width: 140,
                                            render: (date: string, record: LeaveDto) => (
                                                <div>
                                                    <div className="fw-semibold">{dayjs(date).format('DD/MM/YYYY')}</div>
                                                    {record.endTime && (
                                                        <div className="text-muted fs-12">
                                                            <i className="ti ti-clock me-1"></i>
                                                            {record.endTime}
                                                        </div>
                                                    )}
                                                </div>
                                            )
                                        },
                                        {
                                            title: 'Süre',
                                            key: 'duration',
                                            width: 100,
                                            align: 'center' as const,
                                            render: (_: any, record: LeaveDto) => {
                                                const start = dayjs(record.startDate);
                                                const end = dayjs(record.endDate);
                                                const duration = end.diff(start, 'day') + 1;
                                                return (
                                                    <span className="badge bg-light text-dark">
                                                        {duration} gün
                                                    </span>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Durum',
                                            dataIndex: 'status',
                                            key: 'status',
                                            width: 130,
                                            render: (status: string) => {
                                                const statusConfig: { [key: string]: { color: string; text: string; icon: string } } = {
                                                    'approved': { color: 'success', text: 'Onaylandı', icon: 'ti-check' },
                                                    'pending': { color: 'warning', text: 'Beklemede', icon: 'ti-clock' },
                                                    'pending_hierarchy_approval': { color: 'warning', text: 'Onay Bekleniyor', icon: 'ti-clock-hour-4' },
                                                    'rejected': { color: 'error', text: 'Reddedildi', icon: 'ti-x' }
                                                };
                                                const config = statusConfig[status] || { color: 'default', text: status, icon: 'ti-info-circle' };
                                                return (
                                                    <Tag color={config.color} style={{ fontSize: '13px', padding: '4px 10px' }}>
                                                        <i className={`ti ${config.icon} me-1`}></i>
                                                        {config.text}
                                                    </Tag>
                                                );
                                            }
                                        },
                                        {
                                            title: 'Sebep',
                                            dataIndex: 'reason',
                                            key: 'reason',
                                            render: (text: string) => (
                                                <div style={{ 
                                                    wordBreak: 'break-word', 
                                                    whiteSpace: 'normal',
                                                    maxWidth: '300px'
                                                }}>
                                                    {text || <span className="text-muted">-</span>}
                                                </div>
                                            )
                                        }
                                    ]}
                                    pagination={{
                                        pageSize: 10,
                                        showSizeChanger: false,
                                        showTotal: (total) => `Toplam ${total} izin`,
                                        simple: true
                                    }}
                                    size="small"
                                    style={{ marginTop: '20px' }}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="text-center p-5">
                            <div className="avatar avatar-xl mx-auto mb-3 bg-light">
                                <i className="ti ti-inbox fs-48 text-muted"></i>
                            </div>
                            <h5 className="mb-2">İzin Bulunamadı</h5>
                            <p className="text-muted mb-0">Bu çalışan için seçili tarih aralığında izin bulunamadı.</p>
                        </div>
                    )}
                </Modal>
            </div>
            <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                <FooterSection />
            </div>
        </div>
    );
};

export default LeaveReport; 